// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * Minimal user data for references.
 */
export const zUserMinimalBase = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  thumbnailUrl: z.string().nullable(),
  entityType: z.enum(['user']),
});

/**
 * Minimal organization data for references.
 */
export const zOrganizationMinimalBase = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  thumbnailUrl: z.string().nullable(),
  entityType: z.enum(['organization']),
});

/**
 * Base user schema with essential fields for identification and display.
 */
export const zUserBase = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  slug: z.string(),
  thumbnailUrl: z.string().nullable(),
  bannerUrl: z.string().nullable(),
  description: z.string().nullable(),
  email: z.email(),
  entityType: z.enum(['user']),
});

/**
 * Base schema for entities with memberships (e.g. organization).
 */
export const zChannelBase = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  tenantId: z.string(),
  entityType: z.enum(['organization']),
  slug: z.string(),
  thumbnailUrl: z.string().nullable(),
  bannerUrl: z.string().nullable(),
});

/**
 * Base schema for content entities with creator tracking (e.g. page, attachment).
 */
export const zProductBase = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  description: z.string().nullable(),
  createdBy: zUserMinimalBase.nullable(),
  updatedBy: zUserMinimalBase.nullable(),
  entityType: z.enum(['attachment']),
  keywords: z.string(),
});

/**
 * Core membership fields shared across active and inactive memberships.
 */
export const zMembershipBase = z.object({
  id: z.uuid(),
  tenantId: z.string().max(24),
  channelType: z.enum(['organization']),
  channelId: z.uuid(),
  userId: z.uuid(),
  role: z.enum(['admin', 'member']),
  archived: z.boolean(),
  muted: z.boolean(),
  displayOrder: z.number().gte(-140737488355328).lte(140737488355327),
  organizationId: z.uuid(),
});

/**
 * Sync transaction metadata for offline and realtime support, idempotency and HLC-based conflict resolution.
 */
export const zStxBase = z.object({
  mutationId: z.string().max(36),
  sourceId: z.string().max(64),
  fieldTimestamps: z.record(z.string(), z.string()),
  replayed: z.boolean().optional(),
});

/**
 * Boolean query value accepted as a boolean or its lowercase string representation.
 */
export const zBooleanQueryValue = z.union([z.enum(['true', 'false']), z.boolean()]).default('false');

/**
 * Realtime notification delivered via SSE for entity and membership changes.
 */
export const zStreamNotification = z.object({
  kind: z.enum(['product', 'membership']),
  action: z.enum(['create', 'update', 'delete', 'moveOut']),
  productType: z.enum(['attachment']).nullable(),
  resourceType: z
    .enum([
      'request',
      'membership',
      'inactive_membership',
      'tenant',
      'system_role',
      'service_account',
      'api_key',
      'oauth_client',
    ])
    .nullable(),
  subjectId: z.string().nullable(),
  organizationId: z.string().nullable(),
  tenantId: z.string().nullable(),
  channelType: z.enum(['organization']).nullable(),
  path: z.string().nullable(),
  seq: z.int().nullable(),
  channelId: z.string().nullable(),
  stx: zStxBase.nullable(),
  batchUntilSeq: z.int().nullable(),
  count: z.int().nullable(),
  spreadWindow: z.int().nullable(),
  propagation: z
    .object({
      embeddedProduct: z.enum(['attachment']),
      hostProduct: z.enum(['attachment']),
      hostColumn: z.string(),
      update: z.array(z.string()),
      remove: z.array(z.string()),
    })
    .nullable(),
});

/**
 * Standard error response returned by all API endpoints.
 */
export const zApiError = z.object({
  name: z.string(),
  message: z.string(),
  type: z.string(),
  status: z.int().gte(400).lte(599),
  severity: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']),
  entityType: z.enum(['user', 'organization', 'attachment']).optional(),
  logId: z.string().optional(),
  requestId: z.string().optional(),
  path: z.string().optional(),
  method: z.string().optional(),
  timestamp: z.string().optional(),
  userId: z.string().optional(),
  organizationId: z.string().optional(),
  meta: z.record(z.string(), z.union([z.number(), z.string(), z.array(z.string()), z.boolean()]).nullable()).optional(),
});

/**
 * Error returned when the request is malformed or contains invalid data.
 */
export const zBadRequestError = zApiError.and(
  z.object({
    status: z.literal(400).optional(),
  }),
);

/**
 * Error returned when authentication is missing or invalid.
 */
export const zUnauthorizedError = zApiError.and(
  z.object({
    status: z.literal(401).optional(),
  }),
);

/**
 * Error returned when the user lacks permission for the requested action.
 */
export const zForbiddenError = zApiError.and(
  z.object({
    status: z.literal(403).optional(),
  }),
);

/**
 * Error returned when the requested resource cannot be found.
 */
export const zNotFoundError = zApiError.and(
  z.object({
    status: z.literal(404).optional(),
  }),
);

/**
 * Error returned when the request conflicts with current resource state.
 */
export const zConflictError = zApiError.and(
  z.object({
    status: z.literal(409).optional(),
  }),
);

/**
 * Error returned when rate limits are exceeded.
 */
export const zTooManyRequestsError = zApiError.and(
  z.object({
    status: z.literal(429).optional(),
  }),
);

/**
 * A user with profile data and activity timestamps.
 */
export const zUser = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  entityType: z.enum(['user']),
  name: z.string().max(255),
  description: z.string().nullable(),
  slug: z.string().max(255),
  thumbnailUrl: z.string().max(2048).nullable(),
  bannerUrl: z.string().max(2048).nullable(),
  email: z.email(),
  mfaRequired: z.boolean(),
  firstName: z.string().max(255).nullable(),
  lastName: z.string().max(255).nullable(),
  language: z.enum(['en', 'nl']),
  newsletter: z.boolean(),
  userFlags: z.object({
    finishedOnboarding: z.boolean(),
  }),
  updatedAt: z.string().nullable(),
  updatedBy: z.uuid().nullable(),
  lastSeenAt: z.string().nullable(),
  lastStartedAt: z.string().nullable(),
  lastSignInAt: z.string().nullable(),
});

/**
 * The currently authenticated user with their system admin status.
 */
export const zMe = z.object({
  user: zUser,
  isSystemAdmin: z.boolean(),
});

/**
 * Authentication metadata for the current user session.
 */
export const zMeAuthData = z.object({
  enabledOAuth: z.array(z.enum(['github'])),
  hasTotp: z.boolean(),
  sessions: z.array(
    z.object({
      id: z.uuid(),
      type: z.enum(['regular', 'impersonation', 'mfa']),
      userId: z.uuid(),
      deviceName: z.string().max(255).nullable(),
      deviceType: z.enum(['desktop', 'mobile']),
      deviceOs: z.string().max(255).nullable(),
      browser: z.string().max(255).nullable(),
      authStrategy: z.enum(['github', 'google', 'microsoft', 'passkey', 'totp', 'email', 'magic']),
      ipHash: z.string().max(64).nullable(),
      ipSubnetHash: z.string().max(64).nullable(),
      ipCountry: z.string().max(2).nullable(),
      ipAsn: z.int().gte(-2147483648).lte(2147483647).nullable(),
      deviceIdHash: z.string().max(64).nullable(),
      createdAt: z.string(),
      expiresAt: z.string(),
      revokedAt: z.string().nullable(),
      revokedBy: z.uuid().nullable(),
      revocationReason: z
        .enum(['sign_out', 'other_session', 'mfa_enabled', 'session_cap', 'replaced', 'impersonation_stopped'])
        .nullable(),
      impersonatorSessionId: z.uuid().nullable(),
      steppedUpAt: z.string().nullable(),
      steppedUpVia: z.enum(['passkey', 'totp', 'email']).nullable(),
      isCurrent: z.boolean(),
      isNewDevice: z.boolean(),
    }),
  ),
  passkeys: z.array(
    z.object({
      id: z.uuid(),
      userId: z.uuid(),
      deviceName: z.string().max(255).nullable(),
      deviceType: z.enum(['desktop', 'mobile']),
      deviceOs: z.string().max(255).nullable(),
      browser: z.string().max(255).nullable(),
      nameOnDevice: z.string().max(255),
      createdAt: z.string(),
    }),
  ),
});

/**
 * A membership record for a user who has not yet accepted an invitation.
 */
export const zInactiveMembership = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  tenantId: z.string().max(24),
  channelType: z.enum(['organization']),
  channelId: z.uuid(),
  email: z.string().max(255),
  userId: z.uuid().nullable(),
  tokenId: z.uuid().nullable(),
  role: z.enum(['admin', 'member']),
  rejectedAt: z.string().nullable(),
  remindedAt: z.string().nullable(),
  createdBy: zUserMinimalBase.nullable(),
  organizationId: z.uuid(),
});

/**
 * A signed token authorizing file uploads to the configured storage provider.
 */
export const zUploadToken = z.object({
  publicBucket: z.boolean(),
  sub: z.string(),
  s3: z.boolean(),
  signature: z.string().nullable(),
  params: z
    .object({
      auth: z.object({
        key: z.string(),
        expires: z.string().optional(),
      }),
    })
    .nullable(),
});

/**
 * An OAuth consent (grant) of the current user.
 */
export const zConnectedApp = z.object({
  id: z.string(),
  clientId: z.string(),
  clientName: z.string(),
  scopes: z.array(z.string()),
  resources: z.array(z.string()),
  createdAt: z.string(),
  expiresAt: z.string().nullable(),
});

/**
 * A contact or waitlist submission from an unauthenticated user.
 */
export const zRequest = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  message: z.string().max(255).nullable(),
  email: z.string().max(255),
  type: z.enum(['waitlist', 'newsletter', 'contact']),
  wasInvited: z.boolean(),
});

/**
 * A tenant representing an isolated data partition for multi-tenancy.
 */
export const zTenant = z.object({
  id: z.string().max(24),
  name: z.string().max(255),
  status: z.enum(['active', 'suspended', 'archived']),
  restrictions: z.object({
    quotas: z.record(z.string(), z.int().gte(0)),
    rateLimits: z.object({
      apiPointsPerHour: z.int().gte(0),
    }),
    allowUnregisteredClients: z.boolean(),
  }),
  authStrategies: z.array(z.enum(['github', 'google', 'microsoft', 'passkey', 'totp', 'email', 'magic'])),
  createdBy: z.uuid().nullable(),
  subscriptionId: z.string().max(255).nullable(),
  subscriptionStatus: z.enum(['none', 'trialing', 'active', 'past_due', 'paused', 'canceled']),
  subscriptionPlan: z.string().max(255).nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  domainsCount: z.int(),
});

/**
 * A tenant together with the single organization it holds.
 */
export const zTenantWithOrganization = zTenant.and(
  z.object({
    organization: zOrganizationMinimalBase.nullable(),
  }),
);

export const zProtectedResourceMetadata = z.object({
  resource: z.string(),
  authorization_servers: z.array(z.string()),
  scopes_supported: z.array(z.string()),
  bearer_methods_supported: z.array(z.string()),
  resource_documentation: z.string(),
});

/**
 * The main channel entity is an organization.
 */
export const zOrganization = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  entityType: z.enum(['organization']),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  updatedAt: z.string().nullable(),
  slug: z.string().max(255),
  thumbnailUrl: z.string().max(2048).nullable(),
  bannerUrl: z.string().max(2048).nullable(),
  createdBy: zUserMinimalBase.nullable(),
  updatedBy: zUserMinimalBase.nullable(),
  publishedAt: z.string().nullable(),
  publicAt: z.string().nullable(),
  toolsConfig: z.record(
    z.string(),
    z.object({
      order: z.array(z.string()).optional(),
      hidden: z.array(z.string()).optional(),
    }),
  ),
  path: z.string().nullable(),
  shortName: z.string().max(255).nullable(),
  country: z.string().max(255).nullable(),
  timezone: z.string().max(255).nullable(),
  defaultLanguage: z.enum(['en', 'nl']),
  languages: z.array(z.enum(['en', 'nl'])).min(1),
  notificationEmail: z.string().max(255).nullable(),
  color: z.string().max(255).nullable(),
  logoUrl: z.string().max(2048).nullable(),
  websiteUrl: z.string().max(2048).nullable(),
  welcomeText: z.string().max(1000000).nullable(),
  chatSupport: z.boolean(),
  organizationFlags: z.record(z.string(), z.unknown()),
  setupConfig: z.record(z.string(), z.unknown()),
  included: z.object({
    membership: zMembershipBase.optional(),
    counts: z
      .object({
        membership: z.object({
          admin: z.number(),
          member: z.number(),
          pending: z.number(),
          total: z.number(),
        }),
        entities: z.object({
          attachment: z.number(),
        }),
        entitiesSelf: z.object({
          attachment: z.number(),
        }),
        activity: z.object({
          attachment: z.object({
            created: z.number().nullable(),
            updated: z.number().nullable(),
          }),
        }),
      })
      .optional(),
    members: z.array(zUserMinimalBase).optional(),
  }),
});

/**
 * A product entity for file attachment metadata.
 */
export const zAttachment = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  entityType: z.enum(['attachment']),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  updatedAt: z.string().nullable(),
  stx: zStxBase,
  description: z.string().max(1000000).nullable(),
  keywords: z.string().max(1000000),
  createdBy: zUserMinimalBase.nullable(),
  updatedBy: zUserMinimalBase.nullable(),
  deletedAt: z.string().nullable(),
  deletedBy: z.uuid().nullable(),
  publicAt: z.string().nullable(),
  seq: z.int().gte(-9007199254740991).lte(9007199254740991),
  publicBucket: z.boolean(),
  bucketName: z.string().max(255),
  groupId: z.uuid().nullable(),
  filename: z.string().max(255),
  contentType: z.string().max(255),
  convertedContentType: z.string().max(255).nullable(),
  size: z.string().max(255),
  keys: z.object({
    original: z.string(),
    preview: z.string().optional(),
    thumbnail: z.string().optional(),
    converted: z.string().optional(),
  }),
  organizationId: z.uuid(),
  viewCount: z.int().gte(0).optional(),
});

/**
 * The actor an API key runs as, with its role bindings.
 */
export const zServiceAccount = z.object({
  id: z.uuid(),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  status: z.enum(['active', 'disabled']),
  bindings: z.array(
    z.object({
      channelType: z.enum(['organization']),
      channelId: z.string().max(50),
      organizationId: z.string().max(50),
      role: z.enum(['admin', 'member']),
    }),
  ),
  oauthClientId: z.string().max(255).nullable(),
  createdBy: z.uuid().nullable(),
  updatedBy: z.uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
});

/**
 * An API key of a service account; the secret is never returned after creation.
 */
export const zApiKey = z.object({
  id: z.uuid(),
  actorId: z.uuid(),
  tenantId: z.string().max(24),
  name: z.string().max(255),
  prefix: z.string().max(255),
  last4: z.string().max(4),
  scopes: z
    .array(z.enum(['organization:read', 'organization:write', 'attachment:read', 'attachment:write']))
    .nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  revokedBy: z.uuid().nullable(),
  createdBy: z.uuid().nullable(),
  createdAt: z.string(),
});

/**
 * A newly issued API key with its plaintext secret.
 */
export const zCreatedApiKey = zApiKey.and(
  z.object({
    secret: z.string(),
  }),
);

/**
 * Auth health status
 */
export const zGetAuthHealthResponse = z.object({
  restrictedMode: z.boolean(),
  retryAfter: z.number().optional(),
});

export const zCheckEmailBody = z.object({
  email: z.email().min(4).max(255),
});

/**
 * Whether this browser is recognized for the address
 */
export const zCheckEmailResponse = z.object({
  recognized: z.boolean(),
});

export const zInvokeTokenPath = z.object({
  type: z.enum(['oauth-verification', 'invitation', 'magic', 'step-up']),
  token: z.string(),
});

export const zGetTokenDataPath = z.object({
  type: z.enum(['oauth-verification', 'invitation', 'magic', 'step-up']),
  id: z.string().max(50),
});

/**
 * Token is valid
 */
export const zGetTokenDataResponse = z.object({
  email: z.email(),
  userId: z.string().optional(),
  inactiveMembershipId: z.string().optional(),
  invitation: z
    .object({
      entityType: z.enum(['organization']),
      entityName: z.string(),
      role: z.enum(['admin', 'member']),
      inviterName: z.string(),
    })
    .optional(),
});

/**
 * Invitation was accepted
 */
export const zAcceptInvitationTokenResponse = zChannelBase;

export const zStartImpersonationBody = z.object({
  targetUserId: z.string().max(50),
});

/**
 * Impersonating
 */
export const zStartImpersonationResponse = z.void();

/**
 * Stopped impersonating
 */
export const zStopImpersonationResponse = z.void();

export const zResendInvitationWithTokenBody = z.object({
  tokenId: z.uuid(),
});

/**
 * Invitation email sent when the invitation is pending
 */
export const zResendInvitationWithTokenResponse = z.void();

/**
 * User signed out
 */
export const zSignOutResponse = z.void();

export const zSendMagicLinkBody = z.object({
  email: z.email().min(4).max(255),
  redirect: z.string().optional(),
});

/**
 * Magic link email sent (or silently ignored if email not found)
 */
export const zSendMagicLinkResponse = z.void();

/**
 * The full address the held link signs in, as the confirm page shows it
 */
export const zGetPendingMagicLinkResponse = z.object({
  email: z.string(),
});

/**
 * Challenge created
 */
export const zGenerateTotpKeyResponse = z.object({
  totpUri: z.string(),
  manualKey: z.string(),
});

/**
 * TOTP deleted
 */
export const zDeleteTotpResponse = z.void();

export const zCreateTotpBody = z.object({
  code: z.string().regex(/^\d{6}$/),
});

export const zSignInWithTotpBody = z.object({
  code: z.string().regex(/^\d{6}$/),
});

/**
 * TOTP verified
 */
export const zSignInWithTotpResponse = z.void();

export const zCreatePasskeyBody = z.object({
  attestation: z.object({
    id: z.string(),
    rawId: z.string(),
    response: z.object({
      clientDataJSON: z.string(),
      attestationObject: z.string(),
      authenticatorData: z.string().optional(),
      transports: z.array(z.string()).optional(),
      publicKeyAlgorithm: z.number().optional(),
      publicKey: z.string().optional(),
    }),
    authenticatorAttachment: z.enum(['cross-platform', 'platform']).optional(),
    clientExtensionResults: z.unknown().optional(),
    type: z.enum(['public-key']),
  }),
  nameOnDevice: z.string().max(255),
});

/**
 * Passkey created
 */
export const zCreatePasskeyResponse = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  deviceName: z.string().max(255).nullable(),
  deviceType: z.enum(['desktop', 'mobile']),
  deviceOs: z.string().max(255).nullable(),
  browser: z.string().max(255).nullable(),
  nameOnDevice: z.string().max(255),
  createdAt: z.string(),
});

export const zDeletePasskeyPath = z.object({
  id: z.string().max(50),
});

/**
 * Passkey deleted
 */
export const zDeletePasskeyResponse = z.void();

export const zGeneratePasskeyChallengeBody = z.object({
  type: z.enum(['authentication', 'mfa', 'registration']),
});

/**
 * Challenge generated
 */
export const zGeneratePasskeyChallengeResponse = z.object({
  challenge: z.string(),
  credentialIds: z.array(z.string()),
});

export const zSignInWithPasskeyBody = z.object({
  assertion: z.object({
    id: z.string(),
    rawId: z.string(),
    response: z.object({
      clientDataJSON: z.string(),
      authenticatorData: z.string(),
      signature: z.string(),
      userHandle: z.string().optional(),
    }),
    authenticatorAttachment: z.enum(['cross-platform', 'platform']).optional(),
    clientExtensionResults: z.unknown().optional(),
    type: z.enum(['public-key']),
  }),
  type: z.enum(['authentication', 'mfa']),
});

/**
 * Passkey verified
 */
export const zSignInWithPasskeyResponse = z.void();

/**
 * Connect pinned
 */
export const zStartOAuthConnectResponse = z.void();

export const zGithubQuery = z.object({
  type: z.enum(['auth', 'connect', 'invite', 'verify']).optional().default('auth'),
  redirectAfter: z.string().optional(),
});

export const zGoogleQuery = z.object({
  type: z.enum(['auth', 'connect', 'invite', 'verify']).optional().default('auth'),
  redirectAfter: z.string().optional(),
});

export const zMicrosoftQuery = z.object({
  type: z.enum(['auth', 'connect', 'invite', 'verify']).optional().default('auth'),
  redirectAfter: z.string().optional(),
});

export const zGithubCallbackQuery = z.object({
  code: z.string(),
  state: z.string(),
  error: z.string().optional(),
  error_description: z.string().optional(),
  error_uri: z.string().optional(),
});

export const zGoogleCallbackQuery = z.object({
  code: z.string(),
  state: z.string(),
});

export const zMicrosoftCallbackQuery = z.object({
  code: z.string(),
  state: z.string(),
});

/**
 * Step-up state
 */
export const zGetStepUpResponse = z.object({
  steppedUp: z.boolean(),
  methods: z.array(z.enum(['passkey', 'totp', 'email', 'sign_in'])),
});

export const zStepUpBody = z.object({
  passkeyData: z
    .object({
      id: z.string(),
      rawId: z.string(),
      response: z.object({
        clientDataJSON: z.string(),
        authenticatorData: z.string(),
        signature: z.string(),
        userHandle: z.string().optional(),
      }),
      authenticatorAttachment: z.enum(['cross-platform', 'platform']).optional(),
      clientExtensionResults: z.unknown().optional(),
      type: z.enum(['public-key']),
    })
    .optional(),
  totpCode: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
});

/**
 * Session stepped up
 */
export const zStepUpResponse = z.void();

/**
 * Challenge issued
 */
export const zGetStepUpPasskeyChallengeResponse = z.object({
  challenge: z.string(),
  credentialIds: z.array(z.string()),
});

export const zSendStepUpLinkBody = z.object({
  redirect: z.string().optional(),
});

/**
 * Link sent
 */
export const zSendStepUpLinkResponse = z.void();

export const zGetDomainsPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * List of domains
 */
export const zGetDomainsResponse = z.array(
  z.object({
    id: z.uuid(),
    tenantId: z.string().max(24),
    domain: z.string().max(255),
    verified: z.boolean(),
    verificationToken: z.string().max(50).nullable(),
    verifiedAt: z.string().nullable(),
    lastCheckedAt: z.string().nullable(),
    createdAt: z.string(),
  }),
);

export const zCreateDomainBody = z.object({
  domain: z
    .string()
    .min(4)
    .max(255)
    .regex(/^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/),
});

export const zCreateDomainPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Created domain
 */
export const zCreateDomainResponse = z.object({
  id: z.uuid(),
  tenantId: z.string().max(24),
  domain: z.string().max(255),
  verified: z.boolean(),
  verifiedAt: z.string().nullable(),
  lastCheckedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const zDeleteDomainPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Domain removed
 */
export const zDeleteDomainResponse = z.object({
  id: z.uuid(),
  tenantId: z.string().max(24),
  domain: z.string().max(255),
  verified: z.boolean(),
  verifiedAt: z.string().nullable(),
  lastCheckedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const zGetDomainPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Domain with verification token
 */
export const zGetDomainResponse = z.object({
  id: z.uuid(),
  tenantId: z.string().max(24),
  domain: z.string().max(255),
  verified: z.boolean(),
  verificationToken: z.string().max(50).nullable(),
  verifiedAt: z.string().nullable(),
  lastCheckedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const zVerifyDomainPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Verification result
 */
export const zVerifyDomainResponse = z.object({
  success: z.boolean(),
  domain: z.object({
    id: z.uuid(),
    tenantId: z.string().max(24),
    domain: z.string().max(255),
    verified: z.boolean(),
    verificationToken: z.string().max(50).nullable(),
    verifiedAt: z.string().nullable(),
    lastCheckedAt: z.string().nullable(),
    createdAt: z.string(),
  }),
  diagnostics: z
    .object({
      recordsFound: z.array(z.string()),
      expectedToken: z.string(),
    })
    .optional(),
});

export const zCheckSlugBody = z.object({
  slug: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[a-z0-9]+(-{0,3}[a-z0-9]+)*$/),
  entityType: z.enum(['organization']),
});

export const zCheckSlugPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Slug is available
 */
export const zCheckSlugResponse = z.void();

export const zPostAppCatchupBody = z.object({
  cursor: z.string().optional(),
  views: z
    .array(
      z.object({
        key: z.string().max(512),
        organizationId: z.string(),
        prefixes: z.array(z.string().max(512)).min(1).max(64),
        entityTypes: z.array(z.enum(['attachment'])).min(1),
        depth: z.enum(['self', 'subtree']).optional(),
        cursor: z.int().gte(0),
      }),
    )
    .max(256)
    .optional(),
});

/**
 * Catchup summary
 */
export const zPostAppCatchupResponse = z.object({
  changes: z.record(
    z.string(),
    z.object({
      signals: z
        .object({
          membership: z.int().optional(),
        })
        .optional(),
      propagation: z
        .array(
          z.object({
            embeddedProduct: z.enum(['attachment']),
            hostProduct: z.enum(['attachment']),
            hostColumn: z.string(),
            update: z.array(z.string()),
            remove: z.array(z.string()),
          }),
        )
        .optional(),
    }),
  ),
  views: z
    .array(
      z.object({
        key: z.string(),
        status: z.enum(['ok', 'opaque', 'forbidden']),
        frontiers: z.record(z.string(), z.int()).optional(),
        counts: z.record(z.string(), z.int()).optional(),
      }),
    )
    .optional(),
  cursor: z.string().nullable(),
});

/**
 * User deleted
 */
export const zDeleteMeResponse = z.void();

/**
 * User
 */
export const zGetMeResponse = zMe;

export const zUpdateMeBody = z.object({
  bannerUrl: z.string().max(2048).nullish(),
  description: z.string().max(1000000).nullish(),
  firstName: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u)
    .nullish(),
  lastName: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u)
    .nullish(),
  language: z.enum(['en', 'nl']).optional(),
  newsletter: z.boolean().optional(),
  thumbnailUrl: z.string().max(2048).nullish(),
  slug: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[a-z0-9]+(-{0,3}[a-z0-9]+)*$/)
    .optional(),
  userFlags: z
    .object({
      finishedOnboarding: z.boolean().optional(),
    })
    .optional(),
});

/**
 * User
 */
export const zUpdateMeResponse = zUser;

export const zToggleMfaBody = z.object({
  mfaRequired: z.boolean(),
});

/**
 * User
 */
export const zToggleMfaResponse = zUser;

/**
 * User sign-up info
 */
export const zGetMyAuthResponse = zMeAuthData;

/**
 * Invitations pending
 */
export const zGetMyInvitationsResponse = z.object({
  items: z.array(
    z.object({
      entity: zChannelBase,
      inactiveMembership: zInactiveMembership,
    }),
  ),
  total: z.number(),
});

export const zRevokeMySessionsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

/**
 * Sessions were revoked
 */
export const zRevokeMySessionsResponse = z.object({
  data: z.array(
    z.object({
      id: z.uuid(),
      type: z.enum(['regular', 'impersonation', 'mfa']),
      userId: z.uuid(),
      deviceName: z.string().max(255).nullable(),
      deviceType: z.enum(['desktop', 'mobile']),
      deviceOs: z.string().max(255).nullable(),
      browser: z.string().max(255).nullable(),
      authStrategy: z.enum(['github', 'google', 'microsoft', 'passkey', 'totp', 'email', 'magic']),
      ipHash: z.string().max(64).nullable(),
      ipSubnetHash: z.string().max(64).nullable(),
      ipCountry: z.string().max(2).nullable(),
      ipAsn: z.int().gte(-2147483648).lte(2147483647).nullable(),
      deviceIdHash: z.string().max(64).nullable(),
      createdAt: z.string(),
      expiresAt: z.string(),
      revokedAt: z.string().nullable(),
      revokedBy: z.uuid().nullable(),
      revocationReason: z
        .enum(['sign_out', 'other_session', 'mfa_enabled', 'session_cap', 'replaced', 'impersonation_stopped'])
        .nullable(),
      impersonatorSessionId: z.uuid().nullable(),
      steppedUpAt: z.string().nullable(),
      steppedUpVia: z.enum(['passkey', 'totp', 'email']).nullable(),
    }),
  ),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zDeleteMyMembershipQuery = z.object({
  entityId: z.string().max(50),
  entityType: z.enum(['organization']),
});

/**
 * Membership removed
 */
export const zDeleteMyMembershipResponse = z.void();

export const zGetUploadTokenQuery = z.object({
  organizationId: z.uuid().optional(),
  templateId: z.enum(['avatar', 'cover', 'attachment', 'newsletter']),
});

/**
 * Upload token with a scope for a user or organization
 */
export const zGetUploadTokenResponse = zUploadToken;

/**
 * User memberships
 */
export const zGetMyMembershipsResponse = z.object({
  items: z.array(zMembershipBase),
});

/**
 * Connected apps
 */
export const zGetConnectedAppsResponse = z.object({
  items: z.array(zConnectedApp),
});

export const zRevokeConnectedAppPath = z.object({
  id: z.string().max(50),
});

/**
 * Consent was revoked
 */
export const zRevokeConnectedAppResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

/**
 * Public counts
 */
export const zGetPublicCountsResponse = z.object({
  user: z.number(),
  organization: z.number(),
  attachment: z.number(),
});

export const zGetNotificationsQuery = z.object({
  unread: z.enum(['true', 'false']).optional(),
  limit: z.int().gte(1).lte(100).optional().default(30),
  before: z.string().optional(),
});

/**
 * Notifications and unread count
 */
export const zGetNotificationsResponse = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      type: z.enum(['mention', 'comment', 'reply']),
      entityType: z.enum(['attachment']),
      subjectId: z.string(),
      contextId: z.string().nullable(),
      channelId: z.string(),
      channelType: z.enum(['organization']),
      organizationId: z.string(),
      tenantId: z.string(),
      actorId: z.string().nullable(),
      actor: zUserMinimalBase.nullable(),
      channelName: z.string(),
      subjectTitle: z.string(),
      readAt: z.string().nullable(),
    }),
  ),
  unreadCount: z.int().gte(0),
});

export const zMarkNotificationsReadBody = z.object({
  ids: z.array(z.string().max(50)).max(200).optional(),
  contextId: z.string().max(50).optional(),
});

/**
 * Number of notifications marked read
 */
export const zMarkNotificationsReadResponse = z.object({
  updated: z.int().gte(0),
});

/**
 * Notification preferences
 */
export const zGetNotificationPreferencesResponse = z.object({
  mentionEmail: z.boolean(),
  commentEmail: z.boolean(),
  digest: z.enum(['off', 'daily', 'weekly']),
});

export const zUpdateNotificationPreferencesBody = z.object({
  mentionEmail: z.boolean().optional(),
  commentEmail: z.boolean().optional(),
  digest: z.enum(['off', 'daily', 'weekly']).optional(),
});

/**
 * Updated notification preferences
 */
export const zUpdateNotificationPreferencesResponse = z.object({
  mentionEmail: z.boolean(),
  commentEmail: z.boolean(),
  digest: z.enum(['off', 'daily', 'weekly']),
});

export const zUnsubscribeNotificationsQuery = z.object({
  user: z.string().max(50),
  category: z.enum(['digest', 'mention', 'comment', 'newsletter']),
  token: z.string(),
});

/**
 * VAPID public key
 */
export const zGetPushVapidResponse = z.object({
  publicKey: z.string().nullable(),
});

export const zDeletePushSubscriptionQuery = z.object({
  endpoint: z.url().max(2048),
});

/**
 * Number of subscriptions removed
 */
export const zDeletePushSubscriptionResponse = z.object({
  deleted: z.int().gte(0),
});

export const zCreatePushSubscriptionBody = z.object({
  endpoint: z.url().max(2048),
  expirationTime: z.number().nullish(),
  keys: z.object({
    p256dh: z.string().min(1).max(512),
    auth: z.string().min(1).max(512),
  }),
});

/**
 * Stored subscription
 */
export const zCreatePushSubscriptionResponse = z.object({
  id: z.string(),
  endpoint: z.string(),
});

export const zDeleteRequestsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

/**
 * Success
 */
export const zDeleteRequestsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetRequestsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['id', 'email', 'type', 'createdAt']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.string().regex(/^\d+$/).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  seqCursor: z
    .string()
    .regex(/^\d+,\d+$/)
    .optional(),
});

/**
 * Requests
 */
export const zGetRequestsResponse = z.object({
  items: z.array(zRequest),
  total: z.number(),
});

export const zCreateRequestBody = z.object({
  email: z.email().min(4).max(255),
  type: z.enum(['waitlist', 'newsletter', 'contact']),
  message: z.string().max(255).nullable(),
});

/**
 * Request received
 */
export const zCreateRequestResponse = z.void();

/**
 * Unseen counts per parent channel entity per entity type
 */
export const zGetUnseenCountsResponse = z.record(z.string(), z.record(z.string(), z.int().gte(0)));

export const zSystemInviteBody = z.object({
  emails: z.array(z.email().min(4).max(255)).min(1).max(50),
});

/**
 * Invitations are sent
 */
export const zSystemInviteResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  invitesSentCount: z.number(),
});

export const zDeleteUsersBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

/**
 * Success
 */
export const zDeleteUsersResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zUpdateUserBody = z.object({
  bannerUrl: z.string().max(2048).nullish(),
  description: z.string().max(1000000).nullish(),
  firstName: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u)
    .nullish(),
  lastName: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u)
    .nullish(),
  language: z.enum(['en', 'nl']).optional(),
  newsletter: z.boolean().optional(),
  thumbnailUrl: z.string().max(2048).nullish(),
  slug: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[a-z0-9]+(-{0,3}[a-z0-9]+)*$/)
    .optional(),
});

export const zUpdateUserPath = z.object({
  id: z.string().max(50),
});

/**
 * User
 */
export const zUpdateUserResponse = zUser;

export const zSendNewsletterBody = z.object({
  organizationIds: z.array(z.uuid()).max(50),
  roles: z
    .array(z.enum(['admin', 'member']))
    .min(1)
    .max(2),
  subject: z.string().max(255),
  content: z.string().max(1000000),
});

export const zSendNewsletterQuery = z.object({
  toSelf: zBooleanQueryValue.optional(),
});

/**
 * Newsletter sent
 */
export const zSendNewsletterResponse = z.void();

export const zGetTenantsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['createdAt', 'name']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.string().regex(/^\d+$/).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  seqCursor: z
    .string()
    .regex(/^\d+,\d+$/)
    .optional(),
  status: z.enum(['active', 'suspended', 'archived']).optional(),
});

/**
 * Tenants list
 */
export const zGetTenantsResponse = z.object({
  items: z.array(zTenantWithOrganization),
  total: z.number(),
});

export const zSelfCreateTenantBody = z.object({
  name: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u),
});

/**
 * Created tenant
 */
export const zSelfCreateTenantResponse = zTenant;

export const zUpdateTenantBody = z.object({
  name: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u)
    .optional(),
  status: z.enum(['active', 'suspended', 'archived']).optional(),
  subscriptionId: z.string().max(255).nullish(),
  subscriptionStatus: z.enum(['none', 'trialing', 'active', 'past_due', 'paused', 'canceled']).optional(),
  subscriptionPlan: z.string().max(255).nullish(),
  authStrategies: z.array(z.enum(['github', 'google', 'microsoft', 'passkey', 'totp', 'email', 'magic'])).optional(),
  restrictions: z
    .object({
      quotas: z.record(z.string(), z.int().gte(0)).optional(),
      allowUnregisteredClients: z.boolean().optional(),
      rateLimits: z
        .object({
          apiPointsPerHour: z.int().gte(0).optional(),
        })
        .optional(),
    })
    .optional(),
});

export const zUpdateTenantPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Updated tenant
 */
export const zUpdateTenantResponse = zTenant;

export const zGetUsersQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['id', 'name', 'email', 'role', 'createdAt', 'lastSeenAt']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.string().regex(/^\d+$/).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  seqCursor: z
    .string()
    .regex(/^\d+,\d+$/)
    .optional(),
  role: z.enum(['admin']).optional(),
});

/**
 * Users
 */
export const zGetUsersResponse = z.object({
  items: z.array(
    zUserBase.and(
      z.object({
        lastSeenAt: z.string().nullable(),
        role: z.enum(['admin']).nullish(),
      }),
    ),
  ),
  total: z.number(),
});

export const zGetUserPath = z.object({
  relatableUserId: z.string().max(50),
});

export const zGetUserQuery = z.object({
  slug: zBooleanQueryValue.optional(),
});

/**
 * Base user schema with essential fields for identification and display.
 */
export const zGetUserResponse = zUserBase.and(
  z.object({
    lastSeenAt: z.string().nullable(),
  }),
);

export const zGetApiProtectedResourceMetadataPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Protected resource metadata
 */
export const zGetApiProtectedResourceMetadataResponse = zProtectedResourceMetadata;

export const zDeleteOrganizationsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

export const zDeleteOrganizationsPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Success
 */
export const zDeleteOrganizationsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zCreateOrganizationsBody = z
  .array(
    z.object({
      id: z
        .string()
        .max(50)
        .regex(/^temp-/),
      name: z
        .string()
        .min(2)
        .max(255)
        .regex(/^[\p{L}\d\-., '&()]+$/u),
      slug: z
        .string()
        .min(2)
        .max(255)
        .regex(/^[a-z0-9]+(-{0,3}[a-z0-9]+)*$/),
    }),
  )
  .min(1)
  .max(10);

export const zCreateOrganizationsPath = z.object({
  tenantId: z.string().max(50),
});

/**
 * Organizations were created
 */
export const zCreateOrganizationsResponse = z.object({
  data: z.array(
    zOrganization.and(
      z.object({
        included: z
          .object({
            membership: zMembershipBase,
            counts: z
              .object({
                membership: z.object({
                  admin: z.number(),
                  member: z.number(),
                  pending: z.number(),
                  total: z.number(),
                }),
                entities: z.object({
                  attachment: z.number(),
                }),
                entitiesSelf: z.object({
                  attachment: z.number(),
                }),
                activity: z.object({
                  attachment: z.object({
                    created: z.number().nullable(),
                    updated: z.number().nullable(),
                  }),
                }),
              })
              .optional(),
            members: z.array(zUserMinimalBase).optional(),
          })
          .optional(),
      }),
    ),
  ),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetOrganizationsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['id', 'name', 'createdAt', 'displayOrder']).optional().default('displayOrder'),
  order: z.enum(['asc', 'desc']).optional().default('asc'),
  offset: z.string().regex(/^\d+$/).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  seqCursor: z
    .string()
    .regex(/^\d+,\d+$/)
    .optional(),
  relatableUserId: z.uuid().optional(),
  role: z.enum(['admin', 'member']).optional(),
  excludeArchived: z.enum(['true', 'false']).optional(),
  include: z.string().optional(),
});

/**
 * Organizations
 */
export const zGetOrganizationsResponse = z.object({
  items: z.array(zOrganization),
  total: z.number(),
});

export const zGetOrganizationPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

export const zGetOrganizationQuery = z.object({
  slug: zBooleanQueryValue.optional(),
  include: z.string().optional(),
});

/**
 * Organization
 */
export const zGetOrganizationResponse = zOrganization;

export const zUpdateOrganizationBody = z.object({
  slug: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[a-z0-9]+(-{0,3}[a-z0-9]+)*$/)
    .optional(),
  name: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u)
    .optional(),
  shortName: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u)
    .nullish(),
  country: z.string().max(255).nullish(),
  timezone: z.string().max(255).nullish(),
  defaultLanguage: z.enum(['en', 'nl']).optional(),
  languages: z
    .array(z.enum(['en', 'nl']))
    .min(1)
    .optional(),
  notificationEmail: z.string().max(255).nullish(),
  color: z.string().max(255).nullish(),
  thumbnailUrl: z.string().max(2048).nullish(),
  logoUrl: z.string().max(2048).nullish(),
  bannerUrl: z.string().max(2048).nullish(),
  websiteUrl: z.string().max(2048).nullish(),
  welcomeText: z.string().max(1000000).nullish(),
  chatSupport: z.boolean().optional(),
  organizationFlags: z.record(z.string(), z.unknown()).optional(),
  setupConfig: z.record(z.string(), z.unknown()).optional(),
  toolsConfig: z
    .record(
      z.string(),
      z.object({
        order: z.array(z.string()).optional(),
        hidden: z.array(z.string()).optional(),
      }),
    )
    .optional(),
});

export const zUpdateOrganizationPath = z.object({
  tenantId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Organization was updated
 */
export const zUpdateOrganizationResponse = zOrganization;

export const zDeleteAttachmentsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
  stx: z
    .object({
      mutationId: z.string(),
      sourceId: z.string(),
    })
    .optional(),
});

export const zDeleteAttachmentsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Success
 */
export const zDeleteAttachmentsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetAttachmentsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetAttachmentsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['name', 'createdAt', 'contentType']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.string().regex(/^\d+$/).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  seqCursor: z
    .string()
    .regex(/^\d+,\d+$/)
    .optional(),
  channelId: z.uuid().optional(),
});

/**
 * Attachments
 */
export const zGetAttachmentsResponse = z.object({
  items: z.array(zAttachment),
  total: z.number(),
});

export const zCreateAttachmentsBody = z
  .array(
    z.object({
      id: z.uuid(),
      name: z.string().max(255).optional(),
      filename: z.string().max(255),
      contentType: z.string().max(255),
      size: z.string().max(255),
      keys: z.object({
        original: z.string(),
        preview: z.string().optional(),
        thumbnail: z.string().optional(),
        converted: z.string().optional(),
      }),
      groupId: z.uuid().nullish(),
      convertedContentType: z.string().max(255).nullish(),
      publicAt: z.string().nullish(),
      stx: zStxBase,
    }),
  )
  .min(1)
  .max(50);

export const zCreateAttachmentsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zCreateAttachmentsResponse = z.union([
  z.object({
    data: z.array(zAttachment),
    rejectedIds: z.array(z.string()),
    rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  }),
  z.object({
    data: z.array(zAttachment),
    rejectedIds: z.array(z.string()),
    rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  }),
]);

export const zGetPresignedUrlsBody = z.object({
  items: z
    .array(
      z.object({
        attachmentId: z.uuid(),
        variant: z.enum(['original', 'preview', 'thumbnail', 'converted']).optional().default('original'),
      }),
    )
    .min(1)
    .max(50),
});

export const zGetPresignedUrlsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Presigned URLs
 */
export const zGetPresignedUrlsResponse = z.object({
  data: z.array(
    z.object({
      attachmentId: z.uuid(),
      variant: z.enum(['original', 'preview', 'thumbnail', 'converted']),
      url: z.string(),
    }),
  ),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zGetAttachmentPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Attachment
 */
export const zGetAttachmentResponse = zAttachment;

export const zUpdateAttachmentBody = z.object({
  ops: z.object({
    name: z.string().max(255).optional(),
    description: z.string().max(1000000).optional(),
  }),
  stx: zStxBase,
});

export const zUpdateAttachmentPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

export const zUpdateAttachmentQuery = z.object({
  fullResponse: zBooleanQueryValue.optional(),
});

/**
 * Attachment was updated
 */
export const zUpdateAttachmentResponse = zAttachment;

export const zGetMcpProtectedResourceMetadataPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Protected resource metadata
 */
export const zGetMcpProtectedResourceMetadataResponse = zProtectedResourceMetadata;

export const zHandleMcpBody = z.unknown();

export const zHandleMcpPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zDeleteMembershipsBody = z.object({
  ids: z.array(z.string()).min(1).max(50),
});

export const zDeleteMembershipsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zDeleteMembershipsQuery = z.object({
  entityId: z.string().max(50),
  entityType: z.enum(['organization']),
});

/**
 * Success
 */
export const zDeleteMembershipsResponse = z.object({
  data: z.array(z.unknown()),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
});

export const zMembershipInviteBody = z.object({
  emails: z.array(z.email().min(4).max(255)).min(1).max(50),
  role: z.enum(['admin', 'member']),
});

export const zMembershipInvitePath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zMembershipInviteQuery = z.object({
  entityId: z.string().max(50),
  entityType: z.enum(['organization']),
});

/**
 * Created memberships and invite count
 */
export const zMembershipInviteResponse = z.object({
  data: z.array(
    z.object({
      id: z.uuid(),
      tenantId: z.string().max(24),
      channelType: z.enum(['organization']),
      channelId: z.uuid(),
      userId: z.uuid(),
      role: z.enum(['admin', 'member']),
      organizationId: z.uuid(),
      archived: z.boolean().optional(),
      muted: z.boolean().optional(),
      displayOrder: z.number().gte(-140737488355328).lte(140737488355327).optional(),
    }),
  ),
  rejectedIds: z.array(z.string()),
  rejectionReasons: z.record(z.string(), z.array(z.string())).optional(),
  invitesSentCount: z.number(),
});

export const zUpdateMembershipBody = z.object({
  role: z.enum(['admin', 'member']).optional(),
  archived: z.boolean().optional(),
  muted: z.boolean().optional(),
  displayOrder: z.number().gte(-140737488355328).lte(140737488355327).optional(),
});

export const zUpdateMembershipPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Membership updated
 */
export const zUpdateMembershipResponse = z.object({
  createdAt: z.string(),
  id: z.uuid(),
  tenantId: z.string().max(24),
  channelType: z.enum(['organization']),
  channelId: z.uuid(),
  userId: z.uuid(),
  role: z.enum(['admin', 'member']),
  createdBy: z.uuid().nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: z.uuid().nullable(),
  organizationId: z.uuid(),
  archived: z.boolean().optional(),
  muted: z.boolean().optional(),
  displayOrder: z.number().gte(-140737488355328).lte(140737488355327).optional(),
});

export const zHandleMembershipInvitationPath = z.object({
  id: z.string().max(50),
  acceptOrReject: z.enum(['accept', 'reject']),
});

/**
 * Invitation was accepted
 */
export const zHandleMembershipInvitationResponse = zChannelBase;

export const zGetMembersPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetMembersQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z
    .enum(['id', 'name', 'email', 'role', 'createdAt', 'lastSeenAt', 'lastPostedAt'])
    .optional()
    .default('lastSeenAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.string().regex(/^\d+$/).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  seqCursor: z
    .string()
    .regex(/^\d+,\d+$/)
    .optional(),
  entityId: z.string().max(50),
  entityType: z.enum(['organization']),
  role: z.enum(['admin', 'member']).optional(),
  include: z.string().optional(),
  userIds: z.string().optional(),
});

/**
 * Members
 */
export const zGetMembersResponse = z.object({
  items: z.array(
    zUserBase.and(
      z.object({
        lastSeenAt: z.string().nullable(),
        membership: z.object({
          id: z.uuid(),
          tenantId: z.string().max(24),
          channelType: z.enum(['organization']),
          channelId: z.uuid(),
          userId: z.uuid(),
          role: z.enum(['admin', 'member']),
          organizationId: z.uuid(),
          archived: z.boolean().optional(),
          muted: z.boolean().optional(),
          displayOrder: z.number().gte(-140737488355328).lte(140737488355327).optional(),
        }),
        counts: z
          .object({
            memberships: z.record(z.string(), z.unknown()),
            products: z.object({
              attachment: z.number(),
            }),
            activity: z.object({
              attachment: z.number().nullable(),
            }),
          })
          .optional(),
      }),
    ),
  ),
  total: z.number(),
});

export const zGetPendingMembershipsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetPendingMembershipsQuery = z.object({
  q: z.string().max(255).optional(),
  sort: z.enum(['createdAt']).optional().default('createdAt'),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
  offset: z.string().regex(/^\d+$/).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  seqCursor: z
    .string()
    .regex(/^\d+,\d+$/)
    .optional(),
  entityId: z.string().max(50),
  entityType: z.enum(['organization']),
});

/**
 * Pending memberships
 */
export const zGetPendingMembershipsResponse = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      email: z.email(),
      role: z.enum(['admin', 'member']).nullable(),
      createdAt: z.string(),
      createdBy: zUserMinimalBase.nullable(),
    }),
  ),
  total: z.number(),
});

export const zResendPendingInvitationPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Invitation resent
 */
export const zResendPendingInvitationResponse = z.void();

export const zMarkSeenBody = z.object({
  entityIds: z.array(z.string().max(50)).min(1).max(500),
  entityType: z.enum(['attachment']),
});

export const zMarkSeenPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Seen records processed
 */
export const zMarkSeenResponse = z.object({
  newCount: z.int().gte(0),
});

export const zGetServiceAccountsPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetServiceAccountsQuery = z.object({
  q: z.string().max(255).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
});

/**
 * Service accounts
 */
export const zGetServiceAccountsResponse = z.object({
  items: z.array(zServiceAccount),
  total: z.number(),
});

export const zCreateServiceAccountBody = z.object({
  name: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u),
  role: z.enum(['admin', 'member']),
  key: z
    .object({
      name: z
        .string()
        .min(2)
        .max(255)
        .regex(/^[\p{L}\d\-., '&()]+$/u),
      scopes: z
        .array(z.enum(['organization:read', 'organization:write', 'attachment:read', 'attachment:write']))
        .min(1)
        .nullish(),
      expiresAt: z.iso.datetime().optional(),
    })
    .optional(),
});

export const zCreateServiceAccountPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

/**
 * Service account was created
 */
export const zCreateServiceAccountResponse = z.object({
  serviceAccount: zServiceAccount,
  apiKey: zCreatedApiKey.optional(),
});

export const zUpdateServiceAccountBody = z.object({
  name: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u)
    .optional(),
  status: z.enum(['active', 'disabled']).optional(),
});

export const zUpdateServiceAccountPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * Service account was updated
 */
export const zUpdateServiceAccountResponse = zServiceAccount;

export const zGetApiKeysPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * API keys
 */
export const zGetApiKeysResponse = z.object({
  items: z.array(zApiKey),
});

export const zCreateApiKeyBody = z.object({
  name: z
    .string()
    .min(2)
    .max(255)
    .regex(/^[\p{L}\d\-., '&()]+$/u),
  scopes: z
    .array(z.enum(['organization:read', 'organization:write', 'attachment:read', 'attachment:write']))
    .min(1)
    .nullish(),
  expiresAt: z.iso.datetime().optional(),
  rollFrom: z.string().max(50).optional(),
  rollOverlapDays: z.int().gte(0).lte(30).optional().default(7),
});

export const zCreateApiKeyPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
});

/**
 * API key was issued
 */
export const zCreateApiKeyResponse = zCreatedApiKey;

export const zRevokeApiKeyPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
  id: z.string().max(50),
  keyId: z.string().max(50),
});

/**
 * API key was revoked
 */
export const zRevokeApiKeyResponse = zApiKey;

export const zGetYjsTokenPath = z.object({
  tenantId: z.string().max(50),
  organizationId: z.string().max(50),
});

export const zGetYjsTokenQuery = z.object({
  entityType: z.enum(['attachment']),
  entityId: z.string().max(50),
});

/**
 * Yjs auth token
 */
export const zGetYjsTokenResponse = z.object({
  token: z.string(),
});
